<template>
  <div class="ticket-insurance-report-page">
    <router-link class="breadcrumb" :to="{ name: routes.admin.adminDashboard }">
      <span class="breadcrumb-icon">←</span>
      {{ $adminT('adminAll.back_to_operations_console_6f9440', 'Back to Operations Console') }}
    </router-link>

    <h1>{{ $adminT('adminAll.ticket_insurance_report_bc982e', 'Ticket Insurance Report') }}</h1>
    <h2>{{ $adminT('adminAll.period_ea2ad7', 'Period:') }} {{ formattedRange }}</h2>

    <div class="report-tabs" role="tablist">
      <button
        type="button"
        class="report-tab"
        :class="{ 'report-tab--active': activeTab === 'report' }"
        role="tab"
        :aria-selected="activeTab === 'report'"
        @click="activeTab = 'report'"
      >
        {{ $adminT('adminAll.report_ee45c3', 'Report') }}
      </button>
      <button
        type="button"
        class="report-tab"
        :class="{ 'report-tab--active': activeTab === 'withdrawals' }"
        role="tab"
        :aria-selected="activeTab === 'withdrawals'"
        @click="selectWithdrawalsTab"
      >
        {{ $adminT('adminAll.withdrawals_0745d2', 'Withdrawals') }}
      </button>
    </div>

    <div v-if="activeTab === 'report'">
      <div v-if="loading" class="table-wrapper" aria-hidden="true">
        <table class="insurance-table insurance-table--loading">
          <thead>
            <tr>
              <th v-for="column in 6" :key="`head-${column}`">
                <div class="table-skeleton-line table-skeleton-line--header" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in skeletonRows" :key="`row-${row}`">
              <td v-for="column in 6" :key="`row-${row}-${column}`">
                <div class="table-skeleton-line" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="error" class="state-error">{{ adminAllMessage(error) }}</div>
      <div v-else-if="!rows.length" class="state-info">
        {{ $adminT('adminAll.no_insured_tickets_found_in_the_selected_period_5162a3', 'No insured tickets found in the selected period.') }}
      </div>
      <div v-else class="table-wrapper">
        <table class="insurance-table">
          <thead>
            <tr>
              <th>{{ $adminT('adminAll.event_ad8919', 'Event') }}</th>
              <th>{{ $adminT('adminAll.organization_519255', 'Organization') }}</th>
              <th class="align-right">{{ $adminT('adminAll.average_insurance_3ed7f6', 'Average Insurance (€)') }}</th>
              <th class="align-right">{{ $adminT('adminAll.vat_57ab43', 'VAT (%)') }}</th>
              <th class="align-right">{{ $adminT('adminAll.tickets_with_insurance_0c0f13', 'Tickets With Insurance') }}</th>
              <th class="align-right">{{ $adminT('adminAll.total_insurance_6b3466', 'Total Insurance (€)') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.eventId">
              <td>{{ row.eventTitle }}</td>
              <td>{{ row.organizationName }}</td>
              <td class="align-right">{{ formatCurrency(row.averageInsuranceAmount) }}</td>
              <td class="align-right">0.00</td>
              <td class="align-right">{{ row.ticketsWithInsuranceCount }}</td>
              <td class="align-right">{{ formatCurrency(row.totalInsuranceAmount) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th colspan="2">{{ $adminT('adminAll.total_5c6505', 'TOTAL') }}</th>
              <th class="align-right">{{ formatCurrency(totals.averageInsuranceAmount) }}</th>
              <th class="align-right">0.00</th>
              <th class="align-right">{{ totals.ticketsWithInsuranceCount }}</th>
              <th class="align-right">{{ formatCurrency(totals.totalInsuranceAmount) }}</th>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div v-else class="withdrawals-panel">
      <div class="withdrawals-toolbar">
        <label class="withdrawals-status-label" for="withdrawal-status-filter">{{ $adminT('adminAll.status_bae7d5', 'Status') }}</label>
        <select id="withdrawal-status-filter" v-model="withdrawalStatus" class="withdrawals-status-select" @change="loadWithdrawals">
          <option value="">{{ $adminT('adminAll.all_6a7208', 'All') }}</option>
          <option value="RECEIVED">{{ $adminT('adminAll.received_27548c', 'Received') }}</option>
          <option value="FORWARDED">{{ $adminT('adminAll.forwarded_5e96e7', 'Forwarded') }}</option>
          <option value="FORWARD_FAILED">{{ $adminT('adminAll.forward_failed_4427a1', 'Forward failed') }}</option>
          <option value="PROCESSED">{{ $adminT('adminAll.processed_8291b2', 'Processed') }}</option>
        </select>
      </div>

      <div v-if="withdrawalsLoading" class="state-info">{{ $adminT('adminAll.loading_withdrawal_requests_dc4947', 'Loading withdrawal requests…') }}</div>
      <div v-else-if="withdrawalsError" class="state-error">{{ adminAllMessage(withdrawalsError) }}</div>
      <div v-else-if="!withdrawals.length" class="state-info">
        {{ $adminT('adminAll.no_withdrawal_requests_found_for_the_selected_period_and_s_6f45df', 'No withdrawal requests found for the selected period and status.') }}
      </div>
      <div v-else class="table-wrapper">
        <table class="insurance-table">
          <thead>
            <tr>
              <th>{{ $adminT('adminAll.received_at_3b8bf3', 'Received At') }}</th>
              <th>{{ $adminT('adminAll.order_f1e486', 'Order #') }}</th>
              <th>{{ $adminT('adminAll.invoice_f9f388', 'Invoice') }}</th>
              <th>{{ $adminT('adminAll.event_ad8919', 'Event') }}</th>
              <th>{{ $adminT('adminAll.buyer_4186c9', 'Buyer') }}</th>
              <th class="align-right">{{ $adminT('adminAll.premium_ea42e1', 'Premium (€)') }}</th>
              <th>{{ $adminT('adminAll.14_days_b7499e', '14 Days') }}</th>
              <th>{{ $adminT('adminAll.1_month_d2f044', '1+ Month') }}</th>
              <th>{{ $adminT('adminAll.status_bae7d5', 'Status') }}</th>
              <th>{{ $adminT('adminAll.details_dc3dec', 'Details') }}</th>
              <th>{{ $adminT('adminAll.actions_c3cd63', 'Actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in withdrawals" :key="row.id">
              <td>{{ formatDateTime(row.receivedAt) }}</td>
              <td>{{ row.orderId }}</td>
              <td>{{ row.invoiceNumber }}</td>
              <td>{{ row.eventTitle }}</td>
              <td>
                <div>{{ row.buyerName || '—' }}</div>
                <div class="withdrawals-buyer-email">{{ row.buyerEmail }}</div>
              </td>
              <td class="align-right">{{ formatCurrency(row.insuranceAmount) }}</td>
              <td>
                <span class="flag-chip" :class="row.within14Days ? 'flag-chip--green' : 'flag-chip--grey'">
                  {{ row.within14Days ? $adminT('adminAll.da_593c32', 'DA') : $adminT('adminAll.ne_bf0f90', 'NE') }}
                </span>
              </td>
              <td>
                <span class="flag-chip" :class="row.policyMinOneMonth ? 'flag-chip--green' : 'flag-chip--grey'">
                  {{ row.policyMinOneMonth ? $adminT('adminAll.da_593c32', 'DA') : $adminT('adminAll.ne_bf0f90', 'NE') }}
                </span>
              </td>
              <td>
                <span class="status-chip" :class="statusChipClass(row.status)">{{ adminAllLabel(row.status) }}</span>
              </td>
              <td class="withdrawals-details">
                <div v-if="row.message">{{ $adminT('adminAll.message_9e0295', 'Message:') }} {{ row.message }}</div>
                <div v-if="row.buyerConfirmationSentAt">{{ $adminT('adminAll.buyer_email_sent_3382cd', 'Buyer email sent:') }} {{ formatDateTime(row.buyerConfirmationSentAt) }}</div>
                <div v-else>{{ $adminT('adminAll.buyer_email_not_sent_0cc243', 'Buyer email: not sent') }}</div>
                <div v-if="row.buyerConfirmationError">{{ $adminT('adminAll.buyer_email_error_f5b2fa', 'Buyer email error:') }} {{ adminAllMessage(row.buyerConfirmationError) }}</div>
                <div v-if="row.forwardError">{{ $adminT('adminAll.forward_error_98cf79', 'Forward error:') }} {{ adminAllMessage(row.forwardError) }}</div>
                <div v-if="row.forwardClaimedAt">{{ $adminT('adminAll.forward_claimed_acbdf8', 'Forward claimed:') }} {{ formatDateTime(row.forwardClaimedAt) }}</div>
                <div v-if="row.processedAt">{{ $adminT('adminAll.processed_17b9bf', 'Processed:') }} {{ formatDateTime(row.processedAt) }}</div>
              </td>
              <td class="withdrawals-actions">
                <button
                  v-if="canResend(row)"
                  type="button"
                  class="withdrawals-action-button"
                  @click="requestConfirm('resend', row)"
                >
                  {{ resendLabel(row) }}
                </button>
                <button
                  v-if="row.status !== 'PROCESSED'"
                  type="button"
                  class="withdrawals-action-button"
                  @click="requestConfirm('processed', row)"
                >
                  {{ $adminT('adminAll.mark_processed_884124', 'Mark processed') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="confirmAction" class="confirm-overlay">
        <div class="confirm-dialog">
          <p class="confirm-text">{{ confirmMessage }}</p>
          <div class="confirm-buttons">
            <button type="button" class="confirm-button confirm-button--cancel" :disabled="actionSubmitting" @click="cancelConfirm">
              {{ $adminT('adminAll.cancel_77dfd2', 'Cancel') }}
            </button>
            <button type="button" class="confirm-button confirm-button--primary" :disabled="actionSubmitting" @click="runConfirmedAction">
              {{ actionSubmitting ? $adminT('adminAll.saving_56a228', 'Saving…') : $adminT('adminAll.confirm_04a212', 'Confirm') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AdminAllMessages from '@/components/admin/AdminAllMessages'
import moment from 'moment-timezone'
import {
  getTicketInsuranceReport,
  getInsuranceWithdrawals,
  resendInsuranceWithdrawal,
  markInsuranceWithdrawalProcessed,
} from '@/http'

const PRINT_SCROLL_SELECTOR = '.table-wrapper'

export default {
  mixins: [AdminAllMessages],
  provide: { adminLocalization: true },
  name: 'ticket-insurance-report',
  props: {
    from: String,
    to: String,
  },
  data() {
    return {
      loading: false,
      error: '',
      rows: [],
      reportFrom: '',
      reportTo: '',
      totals: {
        averageInsuranceAmount: 0,
        ticketsWithInsuranceCount: 0,
        totalInsuranceAmount: 0,
      },
      printScrollPositions: [],
      beforePrintHandler: null,
      afterPrintHandler: null,
      printMediaQuery: null,
      printMediaQueryHandler: null,
      activeTab: 'report',
      withdrawalsLoaded: false,
      withdrawalsLoading: false,
      withdrawalsError: '',
      withdrawals: [],
      withdrawalStatus: '',
      confirmAction: null,
      actionSubmitting: false,
    }
  },
  computed: {
    formattedRange() {
      const from = this.reportFrom || this.from
      const to = this.reportTo || this.to
      if (!from || !to) {
        return this.$adminT('adminAll.not_selected_183079', 'Not selected')
      }

      return `${this.formatDate(from)} - ${this.formatDate(to)}`
    },
    skeletonRows() {
      return 5
    },
    confirmMessage() {
      if (!this.confirmAction) {
        return ''
      }

      return this.confirmAction.type === 'resend'
        ? this.confirmAction.row.status === 'PROCESSED'
          ? this.$adminT('adminAll.retry_the_buyer_confirmation_for_invoice_value1_1119b2', 'Retry the buyer confirmation for invoice {{value1}}?', { value1: this.confirmAction.row.invoiceNumber })
          : this.$adminT('adminAll.resend_the_withdrawal_notice_for_invoice_value1_to_uniqa_a661b4', 'Resend the withdrawal notice for invoice {{value1}} to UNIQA?', { value1: this.confirmAction.row.invoiceNumber })
        : this.$adminT('adminAll.mark_the_withdrawal_request_for_invoice_value1_as_processe_88db32', 'Mark the withdrawal request for invoice {{value1}} as processed?', { value1: this.confirmAction.row.invoiceNumber })
    },
  },
  watch: {
    from() {
      this.loadReport()
      if (this.withdrawalsLoaded) {
        this.loadWithdrawals()
      }
    },
    to() {
      this.loadReport()
      if (this.withdrawalsLoaded) {
        this.loadWithdrawals()
      }
    },
  },
  created() {
    this.loadReport()
  },
  mounted() {
    if (typeof document !== 'undefined') {
      document.body.classList.add('admin-report-print-layout')
    }
    this.setupPrintHandlers()
  },
  beforeDestroy() {
    this.cleanupPrintHandlers()
    if (typeof document !== 'undefined') {
      document.body.classList.remove('admin-report-print-layout')
    }
  },
  methods: {
    formatDate(value) {
      return moment(value, 'YYYY-MM-DD', true).isValid()
        ? moment(value, 'YYYY-MM-DD').format('DD.MM.YYYY')
        : value
    },
    formatCurrency(value) {
      return Number(value || 0).toFixed(2)
    },
    formatDateTime(value) {
      return value ? moment(value).tz('Europe/Zagreb').format('DD.MM.YYYY HH:mm [Zagreb]') : ''
    },
    canResend(row) {
      if (row.status === 'FORWARD_FAILED' || row.status === 'FORWARDED') {
        return true
      }
      if (row.status === 'PROCESSED') {
        return !row.buyerConfirmationSentAt
      }

      return row.status === 'RECEIVED' && moment().diff(moment(row.receivedAt), 'minutes') >= 15
    },
    resendLabel(row) {
      if (row.status === 'PROCESSED') {
        return this.$adminT('adminAll.retry_buyer_email_6aa997', 'Retry buyer email')
      }

      return row.status === 'RECEIVED' ? this.$adminT('adminAll.recover_forward_2b57f0', 'Recover forward') : this.$adminT('adminAll.resend_0f4f46', 'Resend')
    },
    statusChipClass(status) {
      if (status === 'PROCESSED' || status === 'FORWARDED') {
        return 'status-chip--green'
      }
      if (status === 'FORWARD_FAILED') {
        return 'status-chip--red'
      }

      return 'status-chip--grey'
    },
    selectWithdrawalsTab() {
      this.activeTab = 'withdrawals'
      if (!this.withdrawalsLoaded) {
        this.loadWithdrawals()
      }
    },
    async loadWithdrawals() {
      this.withdrawalsError = ''
      this.withdrawalsLoading = true
      this.withdrawalsLoaded = true

      try {
        const { data } = await getInsuranceWithdrawals(this.from, this.to, this.withdrawalStatus)
        this.withdrawals = data && Array.isArray(data.rows) ? data.rows : []
      } catch (err) {
        const response = err && err.response ? err.response : null
        const responseData = response && response.data ? response.data : null

        this.withdrawalsError =
          (responseData && responseData.message) ||
          (err && err.message) ||
          this.adminAllNotice('Failed to load withdrawal requests.')
      } finally {
        this.withdrawalsLoading = false
      }
    },
    requestConfirm(type, row) {
      this.confirmAction = { type, row }
    },
    cancelConfirm() {
      if (!this.actionSubmitting) {
        this.confirmAction = null
      }
    },
    async runConfirmedAction() {
      if (!this.confirmAction || this.actionSubmitting) {
        return
      }

      this.actionSubmitting = true
      const { type, row } = this.confirmAction

      try {
        if (type === 'resend') {
          await resendInsuranceWithdrawal(row.id)
        } else {
          await markInsuranceWithdrawalProcessed(row.id)
        }
        await this.loadWithdrawals()
      } catch (err) {
        const response = err && err.response ? err.response : null
        const responseData = response && response.data ? response.data : null

        this.withdrawalsError =
          (responseData && responseData.message) ||
          (err && err.message) ||
          this.adminAllNotice('Failed to update the withdrawal request.')
      } finally {
        this.actionSubmitting = false
        this.confirmAction = null
      }
    },
    getPrintScrollContainers() {
      if (typeof document === 'undefined') {
        return []
      }

      return Array.from(document.querySelectorAll(PRINT_SCROLL_SELECTOR))
    },
    preparePrintLayout() {
      this.printScrollPositions = this.getPrintScrollContainers().map(container => {
        const position = {
          container,
          top: container.scrollTop,
          left: container.scrollLeft,
        }

        container.scrollTop = 0
        container.scrollLeft = 0

        return position
      })
    },
    restorePrintLayout() {
      this.printScrollPositions.forEach(({ container, top, left }) => {
        if (!container || !container.isConnected) {
          return
        }

        container.scrollTop = top
        container.scrollLeft = left
      })
      this.printScrollPositions = []
    },
    setupPrintHandlers() {
      if (typeof window === 'undefined') {
        return
      }

      this.beforePrintHandler = () => this.preparePrintLayout()
      this.afterPrintHandler = () => this.restorePrintLayout()
      window.addEventListener('beforeprint', this.beforePrintHandler)
      window.addEventListener('afterprint', this.afterPrintHandler)

      if (typeof window.matchMedia !== 'function') {
        return
      }

      this.printMediaQuery = window.matchMedia('print')
      this.printMediaQueryHandler = event => {
        if (event.matches) {
          this.preparePrintLayout()
        } else {
          this.restorePrintLayout()
        }
      }

      if (typeof this.printMediaQuery.addEventListener === 'function') {
        this.printMediaQuery.addEventListener('change', this.printMediaQueryHandler)
      } else if (typeof this.printMediaQuery.addListener === 'function') {
        this.printMediaQuery.addListener(this.printMediaQueryHandler)
      }
    },
    cleanupPrintHandlers() {
      if (typeof window !== 'undefined') {
        if (this.beforePrintHandler) {
          window.removeEventListener('beforeprint', this.beforePrintHandler)
        }
        if (this.afterPrintHandler) {
          window.removeEventListener('afterprint', this.afterPrintHandler)
        }
      }

      if (this.printMediaQuery && this.printMediaQueryHandler) {
        if (typeof this.printMediaQuery.removeEventListener === 'function') {
          this.printMediaQuery.removeEventListener('change', this.printMediaQueryHandler)
        } else if (typeof this.printMediaQuery.removeListener === 'function') {
          this.printMediaQuery.removeListener(this.printMediaQueryHandler)
        }
      }

      this.restorePrintLayout()
      this.beforePrintHandler = null
      this.afterPrintHandler = null
      this.printMediaQuery = null
      this.printMediaQueryHandler = null
    },
    hasValidRange() {
      return (
        typeof this.from === 'string' &&
        typeof this.to === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(this.from) &&
        /^\d{4}-\d{2}-\d{2}$/.test(this.to) &&
        this.from <= this.to
      )
    },
    async loadReport() {
      this.error = ''
      this.rows = []
      this.reportFrom = this.from || ''
      this.reportTo = this.to || ''
      this.totals = {
        averageInsuranceAmount: 0,
        ticketsWithInsuranceCount: 0,
        totalInsuranceAmount: 0,
      }

      if (!this.hasValidRange()) {
        this.error = this.adminAllNotice('Invalid or missing date range. Use format YYYY-MM-DD and ensure From <= To.')

        return
      }

      this.loading = true
      try {
        const { data } = await getTicketInsuranceReport(this.from, this.to)
        const rows = data && Array.isArray(data.rows) ? data.rows : []
        const totals = data && data.totals ? data.totals : {}

        this.rows = rows
        this.reportFrom = (data && data.from) || this.from
        this.reportTo = (data && data.to) || this.to
        this.totals = {
          averageInsuranceAmount: Number(totals.averageInsuranceAmount || 0),
          ticketsWithInsuranceCount: Number(totals.ticketsWithInsuranceCount || 0),
          totalInsuranceAmount: Number(totals.totalInsuranceAmount || 0),
        }
      } catch (err) {
        const response = err && err.response ? err.response : null
        const responseData = response && response.data ? response.data : null

        this.error =
          (responseData && responseData.message) ||
          (err && err.message) ||
          this.adminAllNotice('Failed to load ticket insurance report.')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ticket-insurance-report-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}
.breadcrumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  color: #2563eb;
  font-weight: 700;
  padding: 0;
  margin: 0 0 12px;
  text-decoration: none;
}
.breadcrumb-icon {
  font-size: 16px;
}
h1 {
  margin: 0 0 8px;
  font-size: 30px;
  color: #0f172a;
}
h2 {
  margin: 0 0 18px;
  font-size: 18px;
  color: #475569;
  font-weight: 600;
}
.state-info,
.state-error {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
  background: #fff;
}
.state-error {
  border-color: #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}
.table-wrapper {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: auto;
  max-height: min(70vh, 640px);
  -webkit-overflow-scrolling: touch;
  background: #fff;
}
.insurance-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  font-size: 14px;
}
th,
td {
  padding: 12px 14px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
}
thead th {
  background: #f8fafc;
  color: #334155;
  position: sticky;
  top: 0;
  z-index: 2;
  background-clip: padding-box;
  box-shadow: inset 0 -1px 0 #d1d5db;
}
tfoot th {
  background: #f8fafc;
  color: #0f172a;
  font-weight: 700;
}
.insurance-table--loading td {
  padding-top: 14px;
  padding-bottom: 14px;
}
.table-skeleton-line {
  width: 100%;
  height: 14px;
  border-radius: 999px;
  background: linear-gradient(90deg, #eef2f7 25%, #e2e8f0 37%, #eef2f7 63%);
  background-size: 400% 100%;
  animation: insurance-table-shimmer 1.4s ease-in-out infinite;
}
.table-skeleton-line--header {
  height: 12px;
}
.align-right {
  text-align: right;
}
.report-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
}
.report-tab {
  background: transparent;
  border: none;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}
.report-tab--active {
  color: #2563eb;
  border-bottom-color: #2563eb;
}
.withdrawals-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}
.withdrawals-status-label {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}
.withdrawals-status-select {
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  background: #fff;
}
.withdrawals-buyer-email {
  font-size: 12px;
  color: #64748b;
}
.withdrawals-details {
  min-width: 180px;
  overflow-wrap: anywhere;
  font-size: 12px;
}
.withdrawals-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.withdrawals-action-button {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #1e293b;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.withdrawals-action-button:hover {
  background: #f1f5f9;
}
.flag-chip,
.status-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}
.flag-chip--green {
  background: #dcfce7;
  color: #166534;
}
.flag-chip--grey {
  background: #e2e8f0;
  color: #475569;
}
.status-chip--green {
  background: #dcfce7;
  color: #166534;
}
.status-chip--red {
  background: #fee2e2;
  color: #b91c1c;
}
.status-chip--grey {
  background: #e2e8f0;
  color: #475569;
}
.confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.confirm-dialog {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  max-width: 360px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.2);
}
.confirm-text {
  margin: 0 0 16px;
  color: #1e293b;
  font-size: 14px;
}
.confirm-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.confirm-button {
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
}
.confirm-button--cancel {
  background: #fff;
  border-color: #cbd5e1;
  color: #1e293b;
}
.confirm-button--primary {
  background: #2563eb;
  color: #fff;
}

@media print {
  .breadcrumb {
    display: none !important;
  }

  .table-wrapper {
    max-height: none !important;
    height: auto !important;
    overflow: visible !important;
  }

  .insurance-table thead th {
    position: static;
    box-shadow: none;
  }

  .insurance-table thead,
  .insurance-table tfoot {
    display: table-row-group !important;
  }

  .insurance-table tr,
  .insurance-table td,
  .insurance-table th {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
}

@keyframes insurance-table-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
@media (max-width: 900px) {
  .ticket-insurance-report-page {
    padding: 16px;
  }
  .insurance-table {
    font-size: 13px;
  }
  th,
  td {
    padding: 10px 12px;
  }
}
@media (max-width: 640px) {
  .ticket-insurance-report-page {
    padding: 14px;
  }
}
</style>

<style>
@media print {
  body.admin-report-print-layout .header,
  body.admin-report-print-layout .footer,
  body.admin-report-print-layout .application-toolbar,
  body.admin-report-print-layout .application-toolbar-zkm,
  body.admin-report-print-layout .back-button-zkm {
    display: none !important;
  }
}
</style>
